import { useRef, useState, type ComponentProps } from 'react';
import { Input } from '@/components/ui/input';
import { useSelectAllOnFocus } from '@/hooks/useSelectAllOnFocus';
import { formatNumber, teamNumberLocale } from '@/lib/formatters';
import { noPasswordManagerProps } from '@/lib/no-password-manager-props';
import { valueOnLeave, valueWhileTyping, type NumberInputRules } from '@/lib/number-input';

type InputProps = Omit<
  ComponentProps<typeof Input>,
  'type' | 'value' | 'defaultValue' | 'onChange' | 'min' | 'max' | 'step'
>;

export type NumberInputProps = InputProps &
  NumberInputRules & {
    value: number | null;
    onValueChange: (next: number | null) => void;
    /** Decimals shown when not typing (default 0 with `integer`, else up to 6). */
    maxFractionDigits?: number;
    /** Thousands separators when not typing (default true). Off for years and codes. */
    grouping?: boolean;
  };

function display(value: number | null, integer: boolean | undefined, maxFractionDigits: number | undefined, grouping: boolean): string {
  if (value == null) return '';
  return formatNumber(value, {
    maximumFractionDigits: maxFractionDigits ?? (integer ? 0 : 6),
    useGrouping: grouping,
  });
}

/**
 * A number that isn't money: counts, days, percentages, sizes (T-176). Money uses
 * MoneyInput. It reads the team's number format, never commits a value just because the
 * field was tabbed through, never jumps to a default when emptied, and keeps what you type on
 * screen until you leave the field: then an out-of-range number is brought to min or max, a
 * fraction is rounded when `integer`, and empty or unreadable text goes back to the value it had.
 *
 * Pass `min`, `max`, `integer` and `allowEmpty` explicitly. Inside a FormField it takes the field's
 * id, `aria-invalid`, `aria-describedby` and `aria-required`.
 */
export function NumberInput({
  value,
  onValueChange,
  min,
  max,
  integer,
  allowEmpty = false,
  maxFractionDigits,
  grouping = true,
  inputMode,
  onFocus,
  onBlur,
  onKeyDown,
  onMouseUp,
  ...props
}: NumberInputProps) {
  const rules: NumberInputRules = { min, max, integer, allowEmpty };
  // The text while typing; null shows the value as formatted. Leaving the field clears it.
  const [draft, setDraft] = useState<string | null>(null);
  // The value when the field was focused, to go back to when it's emptied or unreadable.
  const focusValueRef = useRef<number | null>(value);
  const selectAll = useSelectAllOnFocus();

  const leave = () => {
    if (draft === null) return; // Nothing typed: tabbing through commits nothing.
    const next = valueOnLeave(draft, teamNumberLocale(), rules, focusValueRef.current);
    setDraft(null);
    // Enter settles without leaving the field: what's settled is what a later emptying goes back to.
    focusValueRef.current = next;
    if (!Object.is(next, value)) onValueChange(next);
  };

  return (
    <Input
      type="text"
      inputMode={inputMode ?? (integer && (min ?? -1) >= 0 ? 'numeric' : 'decimal')}
      {...noPasswordManagerProps}
      {...props}
      value={draft ?? display(value, integer, maxFractionDigits, grouping)}
      onFocus={(e) => {
        focusValueRef.current = value;
        selectAll.onFocus(e);
        onFocus?.(e);
      }}
      onMouseUp={(e) => {
        selectAll.onMouseUp(e);
        onMouseUp?.(e);
      }}
      onChange={(e) => {
        const raw = e.target.value;
        setDraft(raw);
        const next = valueWhileTyping(raw, teamNumberLocale(), rules);
        if (next !== undefined && !Object.is(next, value)) onValueChange(next);
      }}
      onKeyDown={(e) => {
        // Enter submits the form: settle the number first, as leaving the field would.
        if (e.key === 'Enter') leave();
        onKeyDown?.(e);
      }}
      onBlur={(e) => {
        leave();
        onBlur?.(e);
      }}
    />
  );
}
